class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.setAttribute('inert', '');
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.onOverlayClick = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.onOverlayClick);
    this.setHeaderCartIconAccessibility();
    this.initShippingDetailsAccessibility();
  }

  initShippingDetailsAccessibility() {
    const shippingDetails = this.querySelector('.cart-drawer__shipping');
    const summary = shippingDetails?.querySelector('summary');
    if (!summary || summary.hasAttribute('role')) return;
    this.setSummaryAccessibility(summary);
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[data-cart-toggle]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.removeAttribute('inert');
    const panel = this.querySelector('.cart-drawer__panel');
    if (panel) panel.removeAttribute('aria-hidden');
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.cart-drawer__panel')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.cart-drawer__panel') || this.querySelector('.cart-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('inert', '');
    const panel = this.querySelector('.cart-drawer__panel');
    if (panel) panel.setAttribute('aria-hidden', 'true');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    if (window.BhcrCartPricing?.syncCartUI) {
      BhcrCartPricing.syncCartUI(parsedState);
    } else if (window.BhcrCartPricing) {
      BhcrCartPricing.syncDrawer(this, parsedState);
    } else {
      const drawerSectionHtml = parsedState.sections?.['cart-drawer'];
      if (drawerSectionHtml) {
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(drawerSectionHtml, section.selector);
          if (window.BhcrDigitAnimate) {
            BhcrDigitAnimate.revealIn(sectionElement);
          }
        });
      }
    }

    setTimeout(() => {
      this.open();
    });
  }

  /**
   * Fallback full refresh from the rendered drawer section. Kept for callers
   * that don't have a cart JSON response on hand.
   */
  refresh() {
    return fetch(`${window.location.pathname || '/'}?sections=cart-drawer`)
      .then((r) => r.json())
      .then((sections) => {
        const html = sections['cart-drawer'];
        if (!html) return;
        if (window.BhcrCartPricing) {
          BhcrCartPricing.syncCartUI({ sections: { 'cart-drawer': html } });
          return;
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newPanel = doc.querySelector('.cart-drawer__panel');
        const oldPanel = this.querySelector('.cart-drawer__panel');
        if (newPanel && oldPanel) oldPanel.innerHTML = newPanel.innerHTML;
        const newDrawer = doc.querySelector('cart-drawer');
        if (newDrawer) this.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      })
      .catch((err) => {
        if (typeof console !== 'undefined' && console.error) console.error('Cart refresh failed:', err);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    if (window.BhcrCartPricing?.getCartSectionsToRender) {
      return BhcrCartPricing.getCartSectionsToRender({ includeFooter: true });
    }

    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {}

customElements.define('cart-drawer-items', CartDrawerItems);
